import { addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { findBlockingApplication, rentalPeriod } from '../utils/rentalPhase';
import {
  ApplicationData,
  ApplicationStatus,
  BackgroundCheck,
  GetApplicationsReturnType
} from './types';

export const makeApplicationInit = async (
  application: ApplicationData
): Promise<void> => {
  try {
    const makeApplication = httpsCallable(functions, 'makeApplication');
    await makeApplication({ application });
  } catch (error) {
    // // console.log('ERROR: makeApplicationInit: ', error);
    throw new Error('Kunde inte skapa ansökan...');
  }
};

export const makeApplicationInitStepTwo = async (
  application: Pick<
    ApplicationData,
    | 'id'
    | 'tenantEmail'
    | 'tenantPersonalNumber'
    | 'tenantFirstName'
    | 'tenantLastName'
    | 'tenantPhoneNumber'
  > & {
    // Apartment information
    squareMeter?: string;
    rooms?: string;
    // Contract period
    contractStartDate?: string;
    contractEndDate?: string;
    // Economic information
    rentalFee?: string;
    deposit?: string;
    clearingnummer?: string;
    bankAccountNumber?: string;
    bankgiro?: string;
    // Included services
    heatingIncluded?: boolean;
    hotWaterIncluded?: boolean;
    electricityIncluded?: boolean;
    internetIncluded?: boolean;
    parkingIncluded?: boolean;
    furnitureIncluded?: boolean;
    // Miscellaneous
    miscellaneousNotes?: string;
  }
): Promise<void> => {
  try {
    const makeApplicationStepTwo = httpsCallable(
      functions,
      'makeApplicationStepTwo'
    );
    await makeApplicationStepTwo({ application });
  } catch (error) {
    console.error('ERROR: makeApplicationInitStepTwo: ', error);
    throw new Error('Kunde inte uppdatera ansökan...');
  }
};

export const removeApplicationInit = async (
  applicationId: string,
  newToDate: string
): Promise<void> => {
  try {
    const removeApplication = httpsCallable(functions, 'RemoveApplication');
    await removeApplication({ applicationId, newToDate });
  } catch (error) {
    // // console.log('ERROR: removeApplication: ', error);
    throw new Error('Kunde inte ta bort uthyrningen');
  }
};

export const tenantAcceptanceInit = async (
  application: Pick<ApplicationData, 'id' | 'tenantAccepted'>
): Promise<void> => {
  try {
    const tenantAcceptance = httpsCallable(functions, 'tenantAcceptance');
    await tenantAcceptance({ application });
  } catch (error) {
    // // console.log('ERROR: tenantAcceptanceInit: ', error);
    throw new Error('Kunde inte hantera hyresgästen...');
  }
};

export const BFRApplicationAcceptanceInit = async (data: {
  applicationId: string;
  isAccepted: boolean;
}): Promise<void> => {
  try {
    const BFRApplicationAcceptance = httpsCallable(
      functions,
      'BFRApplicationAcceptance'
    );

    await BFRApplicationAcceptance(data);
  } catch (error) {
    // // console.log('ERROR: BFRApplicationAcceptanceInit: ', error);
    throw new Error('Kunde inte acceptera ansökan');
  }
};

export const BFRApplicationAcceptanceTwoInit = async (data: {
  applicationId: string;
  isAccepted: boolean;
}): Promise<void> => {
  try {
    const BFRApplicationAcceptanceTwo = httpsCallable(
      functions,
      'BFRApplicationAcceptanceTwo'
    );

    await BFRApplicationAcceptanceTwo(data);
  } catch (error) {
    // // console.log('ERROR: BFRApplicationAcceptanceTwo: ', error);
    throw new Error('Kunde inte acceptera ansökan');
  }
};

export const backgroundCheckInit = async (
  backgroundCheck: BackgroundCheck
): Promise<void> => {
  try {
    const backgroundCheckCall = httpsCallable(functions, 'backgroundCheck');

    await backgroundCheckCall({ backgroundCheck });
  } catch (error) {
    // // console.log('ERROR: backgroundCheck: ', error);
    throw new Error('Kunde inte skicka kontrollen');
  }
};

export const fetchApplications = async (
  uid: string
): Promise<GetApplicationsReturnType> => {
  try {
    const applicationsRef = collection(db, 'applications');
    const q = query(applicationsRef, where('ownedBy', '==', uid));
    const docSnap = await getDocs(q);
    if (docSnap.empty) {
      return { data: [] };
    }
    const data: ApplicationData[] = [];
    docSnap.forEach((doc) => {
      data.push({
        id: doc.id,
        ...doc.data()
      } as ApplicationData);
    });
    return { data };
  } catch (error) {
    // // console.log('ERROR: fetchApplications: ', error);
    return {
      errorMessage: 'Kunde inte hämta ansökningar...'
    };
  }
};

export const fetchApplicationsCreatedBy = async (
  uid: string
): Promise<GetApplicationsReturnType> => {
  try {
    const applicationsRef = collection(db, 'applications');
    const q = query(applicationsRef, where('createdBy', '==', uid));
    const docSnap = await getDocs(q);
    if (docSnap.empty) {
      return { data: [] };
    }
    const data: ApplicationData[] = [];
    docSnap.forEach((doc) => {
      data.push({
        id: doc.id,
        ...doc.data()
      } as ApplicationData);
    });
    return { data };
  } catch (error) {
    // // console.log('ERROR: fetchApplicationsCreatedBy: ', error);
    return {
      errorMessage: 'Kunde inte hämta skapade ansökningar...'
    };
  }
};

export const makeApplicationDirect = async (
  application: ApplicationData
): Promise<{ id: string }> => {
  try {
    console.log('🚀 Creating application directly in Firestore:', application);
    console.log('📝 Firestore DB instance:', db);

    // Backend security check: Prevent duplicate applications
    const applicationsRef = collection(db, 'applications');
    const q = query(applicationsRef, where('createdBy', '==', application.createdBy));
    const existingApplications = await getDocs(q);

    // Spärra bara när PERIODEN krockar med en annan ansökan eller uthyrning på
    // samma lägenhet. Förr spärrade varje ansökan som inte var avslagen, oavsett
    // period: en ägare vars uthyrning tagit slut kunde aldrig ansöka igen, och
    // ingen kunde söka för perioden efter en pågående uthyrning.
    const blocking = findBlockingApplication(
      existingApplications.docs.map((docSnap) => docSnap.data() as ApplicationData),
      application,
    );
    if (blocking) {
      const { start, end } = rentalPeriod(blocking);
      throw new Error(
        `Du har redan en ansökan eller uthyrning för den här lägenheten ${start || '?'} – ${end || 'utan slutdatum'}. ` +
        (end ? `Välj ett startdatum efter ${end}.` : 'Kontakta Hyria om du vill ändra den.'),
      );
    }

    // Create the application document
    const applicationData = {
      ...application,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    console.log('📝 Final application data to save:', applicationData);

    // Add to Firestore applications collection
    const docRef = await addDoc(collection(db, 'applications'), applicationData);

    console.log('✅ Application created with ID:', docRef.id);

    return { id: docRef.id };
  } catch (error: any) {
    console.error('❌ ERROR: makeApplicationDirect: ', error);
    console.error('❌ Error code:', error?.code);
    console.error('❌ Error message:', error?.message);
    console.error('❌ Full error object:', JSON.stringify(error, null, 2));
    throw error; // Re-throw the original error instead of a generic message
  }
};

/**
 * Begär förlängning av befintlig uthyrning
 */
export const requestRentalExtension = async (
  applicationId: string,
  newEndDate: string
): Promise<void> => {
  try {
    const requestExtension = httpsCallable(functions, 'requestRentalExtension');
    await requestExtension({ applicationId, newEndDate });
  } catch (error) {
    console.error('ERROR: requestRentalExtension: ', error);
    throw new Error('Kunde inte begära förlängning...');
  }
};

/**
 * Byt hyresgäst - avslutar nuvarande och skapar ny ansökan
 */
export const changeTenant = async (
  applicationId: string,
  newStartDate: string,
  newEndDate: string,
  tenantInfo: any
): Promise<{ newApplicationId: string }> => {
  try {
    const changeTenantFunc = httpsCallable<
      { applicationId: string; newStartDate: string; newEndDate: string; tenantInfo: any },
      { newApplicationId: string }
    >(functions, 'changeTenant');
    const result = await changeTenantFunc({ applicationId, newStartDate, newEndDate, tenantInfo });
    return result.data;
  } catch (error: any) {
    console.error('ERROR: changeTenant: ', error);
    // Servern säger varför (period utanför den godkända, fel status …) —
    // det beskedet ska fram till ägaren, inte ett generiskt.
    const message = error?.code && error.code !== 'functions/internal' && error?.message
      ? error.message
      : 'Hyresgästbytet kunde inte genomföras. Försök igen, eller hör av dig till Hyria-support.';
    throw new Error(message);
  }
};

/**
 * Initiera uppsägning av uthyrning (med utflyttningsdatum)
 */
export const initiateTermination = async (
  applicationId: string,
  moveOutDate: string,
  reason?: string
): Promise<void> => {
  try {
    const initiateTerminationFunc = httpsCallable(functions, 'initiateTermination');
    await initiateTerminationFunc({ applicationId, moveOutDate, reason });
  } catch (error) {
    console.error('ERROR: initiateTermination: ', error);
    // Serverns felmeddelande (t.ex. vilket datum som faktiskt är tillåtet) ska
    // nå ägaren i stället för att skrivas över med en generisk text.
    const message = error instanceof Error && error.message ? error.message : '';
    throw new Error(message || 'Kunde inte initiera uppsägning.');
  }
};

/**
 * Godkänn förlängningsförfrågan (BRF admin)
 */
export const approveExtensionRequest = async (applicationId: string): Promise<void> => {
  try {
    const approveExtension = httpsCallable(functions, 'approveExtensionRequest');
    await approveExtension({ applicationId });
  } catch (error) {
    console.error('ERROR: approveExtensionRequest: ', error);
    throw new Error('Kunde inte godkänna förlängning...');
  }
};

/**
 * Avslå förlängningsförfrågan (BRF admin)
 */
export const rejectExtensionRequest = async (applicationId: string, reason?: string): Promise<void> => {
  try {
    const rejectExtension = httpsCallable(functions, 'rejectExtensionRequest');
    await rejectExtension({ applicationId, reason });
  } catch (error) {
    console.error('ERROR: rejectExtensionRequest: ', error);
    throw new Error('Kunde inte avslå förlängning...');
  }
};
